import { BrowserProvider, Contract } from "ethers";

const SLIPPAGE_BPS = 50n; // 0.5%
const USDT_ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
];
let poolAbi;

export const minReceived = (quote) => (BigInt(quote.amountOutRaw) * (10000n - SLIPPAGE_BPS)) / 10000n;
export const SLIPPAGE_PERCENT = Number(SLIPPAGE_BPS) / 100;

/** Executes a live FMI<->USDT swap against the on-chain FSCSwap pool. onStep(label) reports progress. */
export async function executePoolSwap({ eip1193, quote, usdtAddress, onStep }) {
  poolAbi = poolAbi || (await (await fetch("/contracts/FSCSwap.json")).json()).abi;
  const signer = await new BrowserProvider(eip1193).getSigner();
  const pool = new Contract(quote.poolAddress, poolAbi, signer);
  const amountIn = BigInt(quote.amountInRaw);
  const minOut = minReceived(quote);

  if (quote.fromToken === "FMI") {
    await ensureGasLeft(new BrowserProvider(eip1193), await signer.getAddress(), amountIn, () => pool.swapFmiForUsdt.estimateGas(minOut, { value: amountIn }));
    onStep("Confirm the swap in your wallet…");
    const tx = await pool.swapFmiForUsdt(minOut, { value: amountIn });
    onStep("Swapping on FSC…");
    await tx.wait();
    return tx.hash;
  }

  const usdt = new Contract(usdtAddress, USDT_ABI, signer);
  const owner = await signer.getAddress();
  if ((await usdt.balanceOf(owner)) < amountIn) throw new Error("Insufficient USDT balance");
  if ((await usdt.allowance(owner, quote.poolAddress)) < amountIn) {
    onStep("Approve USDT spending in your wallet…");
    const approval = await usdt.approve(quote.poolAddress, amountIn);
    onStep("Waiting for approval…");
    await approval.wait();
  }
  onStep("Confirm the swap in your wallet…");
  const tx = await pool.swapUsdtForFmi(amountIn, minOut);
  onStep("Swapping on FSC…");
  await tx.wait();
  return tx.hash;
}

let routerAbi;

/** Buys FMI through FSCBuyRouter: approves the total cost (amount + 0.15% fee + 1 USDT tax) and calls buy(). */
export async function executeBuy({ eip1193, quote, usdtAddress, onStep }) {
  routerAbi = routerAbi || (await (await fetch("/contracts/FSCBuyRouter.json")).json()).abi;
  const signer = await new BrowserProvider(eip1193).getSigner();
  const owner = await signer.getAddress();
  const usdt = new Contract(usdtAddress, USDT_ABI, signer);
  const total = BigInt(quote.totalCostRaw);
  if ((await usdt.balanceOf(owner)) < total) throw new Error("Insufficient USDT balance");
  if ((await usdt.allowance(owner, quote.routerAddress)) < total) {
    onStep("Approve USDT spending in your wallet…");
    const approval = await usdt.approve(quote.routerAddress, total);
    onStep("Waiting for approval…");
    await approval.wait();
  }
  onStep("Confirm the purchase in your wallet…");
  const tx = await new Contract(quote.routerAddress, routerAbi, signer).buy(BigInt(quote.amountInRaw), minReceived(quote));
  onStep("Buying FMI on FSC…");
  await tx.wait();
  return tx.hash;
}

let sellRouterAbi;

/** Sells native FMI through FSCSellRouter: one signed tx, the 0.15% fee + 1 USDT tax come out of the USDT proceeds. */
export async function executeSell({ eip1193, quote, onStep }) {
  sellRouterAbi = sellRouterAbi || (await (await fetch("/contracts/FSCSellRouter.json")).json()).abi;
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const router = new Contract(quote.routerAddress, sellRouterAbi, signer);
  const value = BigInt(quote.amountInRaw);
  await ensureGasLeft(provider, await signer.getAddress(), value, () => router.sell.estimateGas(minReceived(quote), { value }));
  onStep("Confirm the sale in your wallet…");
  const tx = await router.sell(minReceived(quote), { value });
  onStep("Selling FMI on FSC…");
  await tx.wait();
  return tx.hash;
}

/** Throws a clear error when the wallet can't pay `value` plus gas, instead of the node's opaque failure. */
export async function ensureGasLeft(provider, owner, value, estimate) {
  const [balance, fee] = await Promise.all([provider.getBalance(owner), provider.getFeeData()]);
  const gasPrice = fee.maxFeePerGas ?? fee.gasPrice ?? 0n;
  const gas = value < balance ? await estimate().catch(() => 150000n) : 150000n;
  const needed = value + gas * gasPrice;
  if (balance < needed) {
    const keep = Number(needed - value) / 1e18;
    throw new Error(`NOT_ENOUGH_GAS:${(Math.ceil(Math.max(keep, 0.0001) * 1e4) / 1e4).toFixed(4)}`);
  }
}

const isRejection = (err) => err?.code === "ACTION_REJECTED" || err?.info?.error?.code === 4001 || err?.code === 4001;

const errorReason = (err) =>
  err?.reason || err?.data?.message || err?.error?.data?.message || err?.info?.error?.data?.message || err?.info?.error?.data?.originalError?.message ||
  err?.error?.message || err?.info?.error?.message || err?.shortMessage || err?.message || "";

const KNOWN_ERRORS = [
  [/Insufficient USDT balance/, () => "Insufficient USDT balance"],
  [/slippage/i, () => "Price moved more than 0.5%. Get a new quote and try again."],
  [/NOT_ENOUGH_GAS:([\d.]+)/, (r, gasLabel) => `Not enough FMI to cover ${gasLabel}. Keep at least ${r.match(/NOT_ENOUGH_GAS:([\d.]+)/)[1]} FMI for gas (use MAX).`],
  [/insufficient funds|exceeds allowance|out of gas|gas required/i, (_, gasLabel) => `Not enough FMI to cover ${gasLabel}. Lower the amount or use MAX, which keeps some FMI for gas.`],
  [/Internal JSON-RPC error/i, () => "The network rejected the transaction. Lower the amount slightly (keep some FMI for gas) and try again."],
];

/** User-facing message for a failed trade; `noun` is Swap/Purchase/Sale, `gasLabel` what FMI must cover. */
export const tradeErrorMessage = (err, noun = "Swap", gasLabel = "the swap and gas") => {
  if (isRejection(err)) return `${noun} was rejected in your wallet`;
  const reason = errorReason(err);
  const known = KNOWN_ERRORS.find(([re]) => re.test(reason));
  return known ? known[1](reason, gasLabel) : `${noun} failed: ${reason.slice(0, 140) || "unknown error"}`;
};

export const swapErrorMessage = (err) => tradeErrorMessage(err);
